"use client";

import { LocaleLink } from "@i18n/routing";
import { Badge } from "@ui/components/badge";
import { cn } from "@ui/lib";
import {
	AlertTriangleIcon,
	BotIcon,
	CheckCircleIcon,
	CircleHelpIcon,
	ExternalLinkIcon,
	FileTextIcon,
	InfoIcon,
	LinkIcon,
	ShieldIcon,
	XCircleIcon,
} from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";
import type { ReactNode } from "react";
import {
	AI_BOT_GROUPS,
	type AccessTone,
	type AiBotGroupId,
	type CopyFormat,
	type CopyLine,
	accessRecommendationLine,
	accessSummaryLines,
	accessTone,
	blockedByGroup,
	isCloudflareBlocked,
	isUnreachable,
} from "../lib/access-copy";
import type { AiAccessCheckResponse, BlockedBotInfo } from "../lib/api";

interface AiAccessResultsProps {
	result: AiAccessCheckResponse;
}

const ROBOTS_HELP_HREF = "/docs/help/is-robots-txt-blocking-ai-on-your-site";

/**
 * Status tones reuse the AI policy palette the product already uses: Badge
 * `success` / `warning` / `error` (emerald / amber / rose). None of it borrows
 * `positive` / `negative`, which mean "the metric moved".
 */
const TONE: Record<
	AccessTone,
	{
		icon: string;
		panel: string;
		badge: "success" | "warning" | "error" | "info";
		Icon: typeof CheckCircleIcon;
	}
> = {
	green: {
		icon: "text-emerald-500",
		panel: "border-emerald-500/30 bg-emerald-500/10",
		badge: "success",
		Icon: CheckCircleIcon,
	},
	yellow: {
		icon: "text-amber-500",
		panel: "border-amber-500/30 bg-amber-500/10",
		badge: "warning",
		Icon: AlertTriangleIcon,
	},
	red: {
		icon: "text-rose-500",
		panel: "border-rose-500/30 bg-rose-500/10",
		badge: "error",
		Icon: XCircleIcon,
	},
	unknown: {
		icon: "text-muted-foreground",
		panel: "border-border bg-muted/50",
		badge: "info",
		Icon: CircleHelpIcon,
	},
};

const NS = "marketing.aiAccessChecker";

function StatusPill({ tone }: { tone: AccessTone }) {
	const t = useTranslations(NS);
	const { Icon } = TONE[tone];
	return (
		<Badge
			status={TONE[tone].badge}
			className="inline-flex items-center gap-2 px-4 py-2 text-sm normal-case"
		>
			<Icon className="size-4" aria-hidden />
			{t(`status.${tone}`)}
		</Badge>
	);
}

function BotChip({ bot }: { bot: BlockedBotInfo }) {
	return (
		<LocaleLink
			href={`/bots/${bot.slug}`}
			target="_blank"
			rel="noopener noreferrer"
			className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-sm transition-colors hover:border-primary hover:text-primary"
		>
			<BotIcon className="size-3.5" aria-hidden />
			<span>{bot.name}</span>
			{bot.company ? (
				<span className="text-muted-foreground">({bot.company})</span>
			) : null}
			<ExternalLinkIcon className="size-3 opacity-50" aria-hidden />
		</LocaleLink>
	);
}

/**
 * One AI bot role. Every group is always listed, so the reader sees what is
 * open as well as what is blocked, and each says what the bots do and what a
 * block means.
 */
function GroupRow({
	group,
	bots,
	checked,
}: {
	group: (typeof AI_BOT_GROUPS)[number];
	bots: BlockedBotInfo[];
	checked: boolean;
}) {
	const t = useTranslations(NS);
	const blocked = checked && bots.length > 0;
	const tone: AccessTone = !checked
		? "unknown"
		: blocked
			? group.blockedTone
			: "green";
	const { Icon } = TONE[tone];
	const id: AiBotGroupId = group.id;
	return (
		<li
			className={cn(
				"rounded-lg border p-4",
				blocked ? TONE[tone].panel : "bg-background",
			)}
		>
			<div className="flex items-start gap-3">
				<Icon
					className={cn("mt-0.5 size-5 shrink-0", TONE[tone].icon)}
					aria-hidden
				/>
				<div className="min-w-0 flex-1 space-y-2">
					<div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
						<h4 className="font-semibold text-foreground">
							{t(`results.groups.${id}.title`)}
						</h4>
						<span className="text-sm text-muted-foreground">
							{!checked
								? t("results.notChecked")
								: blocked
									? t("results.countBlocked", {
											count: bots.length,
										})
									: t("results.noneBlocked")}
						</span>
					</div>
					<p className="text-sm text-muted-foreground">
						<span className="font-medium text-foreground">
							{t("results.whatTheyDo")}
						</span>{" "}
						{t(`results.groups.${id}.what`)}
					</p>
					<p className="text-sm text-muted-foreground">
						<span className="font-medium text-foreground">
							{t("results.ifBlocked")}
						</span>{" "}
						{t(`results.groups.${id}.ifBlocked`)}
					</p>
					{blocked ? (
						<div className="flex flex-wrap gap-2 pt-1">
							{bots.map((bot) => (
								<BotChip key={bot.slug || bot.name} bot={bot} />
							))}
						</div>
					) : null}
					{blocked && id === "aiSearchIndexers" ? (
						<div className="flex items-start gap-2 rounded-md border bg-background p-3 text-sm text-muted-foreground">
							<InfoIcon
								className="mt-0.5 size-4 shrink-0 text-foreground"
								aria-hidden
							/>
							<p>{t("results.indexerNote")}</p>
						</div>
					) : null}
				</div>
			</div>
		</li>
	);
}

function StatTile({
	icon,
	value,
	label,
}: {
	icon?: ReactNode;
	value?: ReactNode;
	label: string;
}) {
	return (
		<div className="rounded-lg bg-muted/50 p-3 text-center">
			<div className="flex h-8 items-center justify-center text-2xl font-bold tabular-nums">
				{icon ?? value}
			</div>
			<div className="mt-1 text-sm text-muted-foreground">{label}</div>
		</div>
	);
}

function useCopyFormat(): CopyFormat {
	const t = useTranslations(NS);
	const format = useFormatter();
	return {
		list: (names) => format.list(names, { type: "conjunction" }),
		possessive: (name) => t("summary.possessive", { name }),
	};
}

export function AiAccessResults({ result }: AiAccessResultsProps) {
	const t = useTranslations(NS);
	const fmt = useCopyFormat();
	const { domain, analysis, robotsTxtFound, robotsTxtUrl } = result;

	const cloudflareBlocked = isCloudflareBlocked(result);
	const checked = !isUnreachable(result);
	const tone = accessTone(result);
	const blocked = blockedByGroup(result);
	const totalBlockedBots =
		blocked.aiVisitors.length +
		blocked.aiSearchIndexers.length +
		blocked.aiModelTrainers.length;

	const render = (line: CopyLine) => t(line.key, line.values);
	const summaryLines = accessSummaryLines(result, fmt);
	const summary = summaryLines
		? summaryLines.map(render).join(" ")
		: analysis.summary;
	const recommendation = accessRecommendationLine(result, fmt);

	const ok = (
		<CheckCircleIcon
			className={cn("size-6", TONE.green.icon)}
			aria-hidden
		/>
	);
	const bad = (
		<XCircleIcon className={cn("size-6", TONE.red.icon)} aria-hidden />
	);
	const warn = (
		<AlertTriangleIcon
			className={cn("size-6", TONE.yellow.icon)}
			aria-hidden
		/>
	);

	return (
		<div className="space-y-6">
			{/* Header with status */}
			<div className="space-y-4 text-center">
				<StatusPill tone={tone} />

				<div>
					<h3 className="text-2xl font-bold">{domain}</h3>
					<p className="mx-auto mt-1 max-w-2xl text-muted-foreground">
						{summary}
					</p>
				</div>
			</div>

			{/* Cloudflare blocking alert */}
			{cloudflareBlocked && (
				<div className="p-4 rounded-lg border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
					<div className="flex items-start gap-3">
						<ShieldIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
						<div className="flex-1">
							<h4 className="font-semibold text-red-800 dark:text-red-300 mb-1">
								Cloudflare Bot Protection Detected
							</h4>
							<p className="text-sm text-red-700 dark:text-red-300 mb-2">
								This site uses Cloudflare&apos;s bot protection,
								which is blocking access to its robots.txt and
								content. This means AI assistants like ChatGPT,
								Claude, and Perplexity{" "}
								<strong>
									cannot access or cite content from this site
								</strong>
								.
							</p>
							<p className="text-sm text-red-700 dark:text-red-300 mb-3">
								If this is your site, you can configure
								Cloudflare to allow verified AI crawlers while
								keeping protection against malicious bots.
							</p>
							<LocaleLink
								href="/docs/help/cloudflare-is-blocking-access-to-your-site"
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
							>
								Learn how to fix this
								<ExternalLinkIcon className="w-4 h-4" />
							</LocaleLink>
						</div>
					</div>
				</div>
			)}

			{/* Main results card */}
			<div className="bg-card rounded-xl border p-6 space-y-6">
				{cloudflareBlocked ? (
					<>
						{/* Cloudflare-specific stats — robots.txt was inaccessible */}
						<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
							<div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
								<div className="text-2xl font-bold">
									<ShieldIcon className="w-6 h-6 text-red-500 mx-auto" />
								</div>
								<div className="text-sm text-red-700 dark:text-red-300 mt-1 font-medium">
									Cloudflare Blocked
								</div>
							</div>

							<div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
								<div className="text-2xl font-bold">
									<XCircleIcon className="w-6 h-6 text-red-500 mx-auto" />
								</div>
								<div className="text-sm text-red-700 dark:text-red-300 mt-1 font-medium">
									robots.txt Blocked
								</div>
							</div>

							<div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
								<div className="text-2xl font-bold">
									<XCircleIcon className="w-6 h-6 text-red-500 mx-auto" />
								</div>
								<div className="text-sm text-red-700 dark:text-red-300 mt-1 font-medium">
									All Bots Blocked
								</div>
							</div>

							<div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
								<div className="text-2xl font-bold">
									<XCircleIcon className="w-6 h-6 text-red-500 mx-auto" />
								</div>
								<div className="text-sm text-red-700 dark:text-red-300 mt-1 font-medium">
									Spyglasses Blocked
								</div>
							</div>
						</div>

						<p className="text-sm text-muted-foreground">
							Cloudflare&apos;s bot protection is intercepting
							requests before they reach the server. We could not
							read the robots.txt file or any site content. This
							means <strong>all</strong> AI assistants and
							crawlers are being blocked, regardless of what the
							site&apos;s robots.txt may allow.
						</p>

						{/* Links */}
						<div className="flex flex-wrap gap-3 pt-4 border-t">
							<LocaleLink
								href="/docs/help/cloudflare-is-blocking-access-to-your-site"
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
							>
								<LinkIcon className="w-4 h-4" />
								How to fix Cloudflare blocking
								<ExternalLinkIcon className="w-3 h-3" />
							</LocaleLink>
						</div>
					</>
				) : (
					<>
						<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
							<StatTile
								icon={
									!checked ? bad : robotsTxtFound ? ok : warn
								}
								label={
									!checked
										? t("results.robotsTxtUnreachable")
										: robotsTxtFound
											? t("results.robotsTxtFound")
											: t("results.robotsTxtNotFound")
								}
							/>
							<StatTile
								value={checked ? totalBlockedBots : "–"}
								label={t("results.botsBlocked")}
							/>
							<StatTile
								icon={
									checked
										? analysis.hasWildcardDisallow
											? bad
											: ok
										: undefined
								}
								value="–"
								label={
									!checked
										? t("results.wildcardRule")
										: analysis.hasWildcardDisallow
											? t("results.wildcardDisallow")
											: t("results.noWildcardDisallow")
								}
							/>
							<StatTile
								icon={
									checked
										? analysis.isSpyglassesAllowed
											? ok
											: bad
										: undefined
								}
								value="–"
								label={t("results.spyglassesAccess")}
							/>
						</div>

						<div className="space-y-3">
							<h4 className="font-semibold">
								{t("results.groupsTitle")}
							</h4>
							<ul className="space-y-3">
								{AI_BOT_GROUPS.map((group) => (
									<GroupRow
										key={group.id}
										group={group}
										bots={blocked[group.id]}
										checked={checked}
									/>
								))}
							</ul>
						</div>

						<div className="flex flex-wrap gap-3 border-t pt-4">
							{checked && robotsTxtFound ? (
								<a
									href={robotsTxtUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary"
								>
									<FileTextIcon
										className="size-4"
										aria-hidden
									/>
									{t("results.viewRobotsTxt")}
									<ExternalLinkIcon
										className="size-3"
										aria-hidden
									/>
								</a>
							) : null}

							{tone === "red" || tone === "yellow" ? (
								<LocaleLink
									href={ROBOTS_HELP_HREF}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary"
								>
									<LinkIcon className="size-4" aria-hidden />
									{t("results.learnMore")}
									<ExternalLinkIcon
										className="size-3"
										aria-hidden
									/>
								</LocaleLink>
							) : null}
						</div>
					</>
				)}
			</div>

			{/* Recommendation (skipped when the Cloudflare alert is shown) */}
			{recommendation && (tone === "red" || tone === "yellow") ? (
				<div className={cn("rounded-lg border p-4", TONE[tone].panel)}>
					<h4 className="mb-2 font-semibold">
						{t("results.recommendation")}
					</h4>
					<p className="text-sm text-muted-foreground">
						{render(recommendation)}
					</p>
					<LocaleLink
						href={ROBOTS_HELP_HREF}
						target="_blank"
						rel="noopener noreferrer"
						className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
					>
						{t("results.howToFix")}
						<ExternalLinkIcon className="size-4" aria-hidden />
					</LocaleLink>
				</div>
			) : null}
		</div>
	);
}
